import translate from '@translate';
import React, { FC, useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setLocationParams } from 'src/resources/location/reducer';
import { RootStoreType } from 'src/types/store';
import { OrderMessagesOnExpeditionBoxType } from 'src/types/orderMessagesOnExpeditionBox';
import { checkFormValidity, getEditData } from 'src/utils/formFunctions';
import Loading from 'src/components/Loading/intex';
import { useCheckPermission } from 'src/utils/checkPermission';
import { useNavigate } from 'react-router-dom';
import TopBlock from './TopBlock';
import { EditKeysType, EDIT_KEYS } from './constants';
import { addMessage } from 'src/resources/alertMessages/reducer';
import RenderComponents from './RenderComponents';
import {
    clearOrderMessagesOnExpeditionBoxDetail,
    getOrderMessagesOnExpeditionBoxDetailFetch,
    createOrderMessagesOnExpeditionBoxDetailFetch,
    updateOrderMessagesOnExpeditionBoxDetailFetch,
    deleteOrderMessagesOnExpeditionBoxDetailFetch,
} from 'src/resources/orderMessagesOnExpeditionBoxDetail/reducer';
import ConfirmDialog, { ConfirmDialogStatePropsType } from 'src/components/ConfirmDialog';
import { FormErrorsType, OnChangeType } from '../../types/global';

const initData = {};

const OrderMessagesOnExpeditionBoxDetailContainer: FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { idOrderMessagesOnExpeditionBox } = useParams();

    const newOrderMessagesOnExpeditionBox = !idOrderMessagesOnExpeditionBox;

    const formRef = useRef<HTMLFormElement>(null);

    const data = useSelector((state: RootStoreType) => state.orderMessagesOnExpeditionBoxDetail.data);
    const loading = useSelector((state: RootStoreType) => state.orderMessagesOnExpeditionBoxDetail.loading);
    const deleted = useSelector((state: RootStoreType) => state.orderMessagesOnExpeditionBoxDetail.deleted);

    const locationHistory = useSelector((state: RootStoreType) => state.location.history);

    const [editData, setEditData] = useState<OrderMessagesOnExpeditionBoxType>(getEditData(initData || {}, EDIT_KEYS));
    const [editErrors, setEditErrors] = useState<FormErrorsType>({});
    const [confirmParams, setConfirmParams] = useState<ConfirmDialogStatePropsType | null>(null);

    const permissionCreate = useCheckPermission('MESSAGES_ON_EXPEDITION_BOX.CREATE');
    const permissionUpdate = useCheckPermission('MESSAGES_ON_EXPEDITION_BOX.UPDATE');
    const permissionDelete = useCheckPermission('MESSAGES_ON_EXPEDITION_BOX.DELETE');

    const disableEdit = newOrderMessagesOnExpeditionBox ? !permissionCreate : !permissionUpdate;

    const backUrl = '/orders/messages_on_expedition_box/';

    useEffect(() => {
        if (idOrderMessagesOnExpeditionBox) {
            dispatch(
                setLocationParams({
                    name: translate('orderMessagesOnExpeditionBoxDetail.title', { idOrderMessagesOnExpeditionBox }),
                    backUrl,
                })
            );
        } else {
            dispatch(setLocationParams({ name: translate('orderMessagesOnExpeditionBoxDetail.titleNew'), backUrl }));
        }
    }, [idOrderMessagesOnExpeditionBox, backUrl, locationHistory]);

    useEffect(() => {
        if (idOrderMessagesOnExpeditionBox) {
            dispatch(getOrderMessagesOnExpeditionBoxDetailFetch(idOrderMessagesOnExpeditionBox));
        } else {
            setEditData(initData);
        }
        return () => {
            dispatch(clearOrderMessagesOnExpeditionBoxDetail());
        };
    }, [idOrderMessagesOnExpeditionBox]);

    useEffect(() => {
        if (data) {
            setEditData({ ...data });
        }

        if (!idOrderMessagesOnExpeditionBox && data?.ID) {
            navigate('/orders/messages_on_expedition_box/' + String(data.ID));
        }
    }, [data]);

    useEffect(() => {
        if (deleted) {
            navigate(backUrl);
        }
    }, [deleted]);

    const onChange: OnChangeType<EditKeysType> = (key, value, error) => {
        const newData = { ...editData };
        const newErrors = { ...editErrors };
        newData[key] = value;
        newErrors[key] = error;
        setEditData(newData);
        setEditErrors(newErrors);
    };

    const handleSave = () => {
        const newFormErrors = checkFormValidity(formRef);

        if (!Object.keys(newFormErrors).length) {
            const formatData = { ...editData };

            if (newOrderMessagesOnExpeditionBox) {
                dispatch(createOrderMessagesOnExpeditionBoxDetailFetch(formatData));
            } else {
                dispatch(
                    updateOrderMessagesOnExpeditionBoxDetailFetch({
                        idOrderMessagesOnExpeditionBox,
                        data: formatData,
                    })
                );
            }
        } else {
            dispatch(
                addMessage({
                    type: 'warning',
                    title: translate('messages.formWarningTitle'),
                    text: translate('messages.formWarningText'),
                })
            );
        }

        setEditErrors(newFormErrors);
    };

    const handleDelete = () => {
        setConfirmParams({
            title: translate('orderMessagesOnExpeditionBoxDetail.action.deleteMessageOnExpeditionBoxDetail'),
            content: translate('orderMessagesOnExpeditionBoxDetail.action.deleteMessageOnExpeditionBoxDetailConfirm'),
            action: () => dispatch(deleteOrderMessagesOnExpeditionBoxDetailFetch(idOrderMessagesOnExpeditionBox)),
        });
    };

    return (
        <>
            <TopBlock
                data={data}
                idOrderMessagesOnExpeditionBox={idOrderMessagesOnExpeditionBox}
                disableEdit={disableEdit}
                permissionDelete={permissionDelete}
                handleSave={handleSave}
                handleDelete={handleDelete}
                newOrderMessagesOnExpeditionBox={newOrderMessagesOnExpeditionBox}
            />

            {loading && <Loading />}
            <RenderComponents
                formRef={formRef}
                data={data}
                onChange={onChange}
                editData={editData}
                editErrors={editErrors}
                disableEdit={disableEdit}
            />
            {confirmParams && (
                <ConfirmDialog
                    title={confirmParams.title}
                    content={confirmParams.content}
                    action={confirmParams.action}
                    onClose={(): any => setConfirmParams(null)}
                />
            )}
        </>
    );
};

export default OrderMessagesOnExpeditionBoxDetailContainer;
